import {useInfiniteQuery} from '@tanstack/react-query';
import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
import {sortRecords} from '@flexinet/ui-components';
import {fetchRecords} from '../services/BillableEventsService';
import {BillableEvent} from '../types';
import {useBillableEventsFilter} from './useBillableEventsFilter';
import {useBillableEventsSearch} from './useBillableEventsSearch';

// FNP-533: read-only list. The two admin row actions (requeue, write-off) live in the
// columns hook; this hook is list-fetch, keyword, sort.

// DEFAULT: newest first on `created`. The schema types it nullable, but it is the range
// key of every index the three list queries read (record-type-created-index,
// state-createdAt-index, channel-createdAt-index), so a row without it cannot be listed.
// It also matches the API's own order (scanIndexForward: false), so the first paint does
// not reshuffle what the server returned. Never `updated`: it moves under the reader.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<BillableEvent> = {
    columnAccessor: 'created',
    direction: 'desc',
};

const PAGE_SIZE = 10;

const matchesKeyword = (item: BillableEvent, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.id,
        item.customerId,
        item.channel,
        item.state,
        item.meteringRecordId,
        item.meterId,
        item.reason,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase().includes(needle));
};

export const useBillableEvents = () => {
    const {filters} = useBillableEventsFilter();
    const {keyword} = useBillableEventsSearch();

    // Component state, not the module store: sort is how ONE operator is viewing the
    // table right now, unlike keyword and filters, which sibling components share.
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<BillableEvent>>(DEFAULT_SORT_STATUS);

    // Filters are server-side, so they are in the key. The keyword is NOT: it filters the
    // loaded rows client-side, and keeping it out of the key means Reload and the table
    // always share one cache entry.
    const billableEventsQuery = useInfiniteQuery({
        queryKey: ['billableEvents', 'list', filters, PAGE_SIZE],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit: PAGE_SIZE, filter: filters, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // FNP-539: the service guarantees page.items is an array even on a GraphQL-level
    // error; filter(Boolean) keeps an undefined row out of the table as a backstop.
    const items: BillableEvent[] = (billableEventsQuery.data?.pages?.flatMap(
        page => (page && 'items' in page) ? page.items : []
    ) ?? []).filter(Boolean);

    // Keyword and sort are client-side, over the pages LOADED SO FAR. Token pagination
    // cannot filter or reorder rows the browser has not fetched; that is a property of
    // the model, not an oversight. The API ignores `sort`, so there is no server sort.
    const needle = keyword.trim().toLowerCase();
    const visibleItems = sortRecords(items.filter(item => matchesKeyword(item, needle)), sortStatus);

    return {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading: billableEventsQuery.isLoading,
        isError: billableEventsQuery.isError,
        isFetchingMore: billableEventsQuery.isFetchingNextPage,
        nextToken: billableEventsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            if (billableEventsQuery.hasNextPage && !billableEventsQuery.isFetchingNextPage) {
                billableEventsQuery.fetchNextPage();
            }
        },
        reload: () => {
            billableEventsQuery.refetch();
        },
    };
};
